import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Star, ChevronDown, ChevronUp,
  Sun, Wind, Flame, Home, Compass, Users, MapPin, Award,
  ArrowUpCircle, Layers, Leaf, Shield, Zap
} from "lucide-react";

// ─── Stats Counter ───────────────────────────────────────────────
function useCountUp(target, duration = 2000, started = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!started) return;
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, started]);
  return count;
}

// ─── FAQ Item ───────────────────────────────────────────────────
function FAQItem({ question, answer, isOpen, onClick }) {
  return (
    <div
      className={`rounded-2xl border transition-all duration-300 ${
        isOpen ? "border-vastu-terracotta/30 shadow-md" : "border-vastu-border"
      }`}
      style={{ background: isOpen ? "rgba(210, 124, 44, 0.03)" : "white" }}
    >
      <button
        onClick={onClick}
        className="w-full px-6 py-5 flex items-center justify-between text-left"
        data-testid="faq-toggle"
      >
        <span className="font-body font-semibold text-vastu-charcoal pr-4">{question}</span>
        <span className="flex-shrink-0" style={{ color: "#D27C2C" }}>
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </span>
      </button>
      {isOpen && (
        <div className="px-6 pb-5">
          <p className="font-body text-vastu-mutedFg leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

// ─── Main HomePage ───────────────────────────────────────────────
export default function HomePage() {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef(null);

  const projects = useCountUp(2400, 2000, statsVisible);
  const clients = useCountUp(1800, 2000, statsVisible);
  const cities = useCountUp(120, 1500, statsVisible);
  const experts = useCountUp(15, 1500, statsVisible);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const vastuPrinciples = [
    {
      icon: ArrowUpCircle,
      direction: "North",
      deity: "Kuber – Lord of Wealth",
      color: "#3A5A40",
      bg: "rgba(58, 90, 64, 0.08)",
      desc: "Keep this zone open and clutter-free to invite financial prosperity and career growth into your home.",
      rooms: "Living Room, Office, Treasury",
    },
    {
      icon: Sun,
      direction: "East",
      deity: "Indra – Lord of Power",
      color: "#E9A030",
      bg: "rgba(233, 160, 48, 0.08)",
      desc: "The rising sun enters from the East, bringing health, vitality, and positive solar energy.",
      rooms: "Main Door, Pooja Room, Windows",
    },
    {
      icon: Flame,
      direction: "South-East",
      deity: "Agni – Lord of Fire",
      color: "#D27C2C",
      bg: "rgba(210, 124, 44, 0.08)",
      desc: "The fire zone — ideal for kitchen placement to harness transformative and energizing energy.",
      rooms: "Kitchen, Generator, Electrical",
    },
    {
      icon: Wind,
      direction: "North-East",
      deity: "Ishaan – Most Sacred",
      color: "#2D7D46",
      bg: "rgba(45, 125, 70, 0.08)",
      desc: "The most sacred zone in Vastu. Keep it clean, open, and use it for prayer or meditation.",
      rooms: "Pooja Room, Meditation, Water",
    },
    {
      icon: Compass,
      direction: "South-West",
      deity: "Nirrti – Stability",
      color: "#8B6914",
      bg: "rgba(139, 105, 20, 0.08)",
      desc: "Heavy structures here provide stability and strength to the household's relationships and health.",
      rooms: "Master Bedroom, Heavy Storage",
    },
    {
      icon: Layers,
      direction: "West",
      deity: "Varuna – Lord of Water",
      color: "#6B9AC4",
      bg: "rgba(107, 154, 196, 0.08)",
      desc: "The West is associated with gains and profits. Children's rooms here enhance focus and learning.",
      rooms: "Children's Room, Study, Dining",
    },
  ];

  const benefits = [
    { icon: Zap, title: "Enhanced Energy Flow", desc: "Properly aligned spaces ensure positive prana (life energy) flows freely throughout your home.", color: "#D27C2C" },
    { icon: Shield, title: "Harmonious Family Life", desc: "Vastu-compliant designs reduce stress, improve relationships, and create a peaceful living environment.", color: "#3A5A40" },
    { icon: Sun, title: "Health & Wellbeing", desc: "Strategic room placement maximizes natural light and ventilation, promoting physical and mental health.", color: "#E9A030" },
    { icon: Award, title: "Financial Prosperity", desc: "Align your wealth zones with Vastu principles to attract abundance and financial stability.", color: "#2D7D46" },
    { icon: Leaf, title: "Sustainable Design", desc: "Vastu principles naturally emphasize eco-friendly design using natural light, air, and materials.", color: "#6B9AC4" },
    { icon: Home, title: "Customized Floor Plans", desc: "Every plan is tailored to your unique plot dimensions, direction, and family requirements.", color: "#8B6914" },
  ];

  const howItWorks = [
    {
      step: "01",
      title: "Share Your Plot Details",
      desc: "Fill out our simple form with your plot dimensions, facing direction, and the number of rooms and floors you need.",
      icon: MapPin,
      color: "#D27C2C",
    },
    {
      step: "02",
      title: "Expert Vastu Analysis",
      desc: "Our certified Vastu consultants analyze your plot's orientation, calculate the Brahmasthan, and map the energy zones.",
      icon: Compass,
      color: "#3A5A40",
    },
    {
      step: "03",
      title: "Receive Your Floor Plan",
      desc: "Get a beautifully crafted, Vastu-compliant floor plan within 24-48 hours, complete with room placement guidelines.",
      icon: CheckCircle2,
      color: "#E9A030",
    },
  ];

  const testimonials = [
    {
      name: "Priya Sharma",
      location: "Pune, Maharashtra",
      text: "After moving into our VastuNet-designed home, the positive energy is palpable. My husband's business has flourished and the family feels more connected. Truly life-changing!",
      rating: 5,
    },
    {
      name: "Rajesh Gupta",
      location: "Jaipur, Rajasthan",
      text: "We had our plot facing North-West and were confused. VastuNet's expert gave us a detailed plan that resolved all our concerns. The house feels incredibly peaceful.",
      rating: 5,
    },
    {
      name: "Anita Krishnamurthy",
      location: "Bengaluru, Karnataka",
      text: "The level of detail in the floor plan was exceptional. Every room's placement was explained with the Vastu logic behind it. I felt confident in every construction decision.",
      rating: 5,
    },
  ];

  const faqs = [
    {
      question: "What is Vastu Shastra?",
      answer: "Vastu Shastra is an ancient Indian science of architecture and construction that harmonizes a structure with the five elements of nature (Pancha Bhuta) — Earth, Water, Fire, Air, and Space. Dating back over 5,000 years, it provides guidelines on building design, layout, and spatial arrangement to promote well-being, prosperity, and peace.",
    },
    {
      question: "Does Vastu really make a difference?",
      answer: "Thousands of families across India have experienced positive changes after adopting Vastu principles. Modern science acknowledges that factors like natural light, ventilation, and orientation significantly impact human psychology and health — all of which Vastu addresses systematically. Our clients consistently report improved family harmony, better health, and enhanced prosperity.",
    },
    {
      question: "What information do I need to share for a floor plan?",
      answer: "You need to share your plot's length and width (in feet), the direction your plot faces (North, South, East, West, or diagonal), the number of bedrooms/rooms you need, and the number of floors. Our form makes this simple and straightforward.",
    },
    {
      question: "How long does it take to receive my floor plan?",
      answer: "Typically, our Vastu experts analyze your plot and prepare a customized floor plan within 24-48 business hours. For complex plots or multi-floor structures, it may take up to 72 hours. We'll keep you informed throughout the process.",
    },
    {
      question: "Can Vastu be applied to an existing house?",
      answer: "Yes! Vastu corrections can be made to existing homes through remedies that don't require major structural changes. These can include strategic placement of furniture, mirrors, plants, colors, and certain objects to balance the energy. However, applying Vastu from scratch during construction yields the best results.",
    },
    {
      question: "Is your service available across India?",
      answer: "Absolutely. VastuNet serves clients pan-India. Our online service means you can submit your enquiry from anywhere — whether you're in a metro city or a small town — and receive expert Vastu consultation and floor plans digitally.",
    },
  ];

  return (
    <div data-testid="home-page">

      {/* ─── HERO ─────────────────────────────────────────────── */}
      <section
        className="relative min-h-screen flex items-center overflow-hidden"
        style={{ background: "linear-gradient(135deg, #2D2A26 0%, #1a1816 50%, #3A5A40 100%)" }}
        data-testid="hero-section"
      >
        {/* Blueprint grid overlay */}
        <div className="absolute inset-0 blueprint-grid opacity-30" />

        {/* Animated circles */}
        <div className="absolute top-20 right-20 w-96 h-96 rounded-full opacity-10 animate-float"
          style={{ background: "radial-gradient(circle, #D27C2C, transparent)" }} />
        <div className="absolute bottom-20 left-10 w-64 h-64 rounded-full opacity-8 animate-float delay-500"
          style={{ background: "radial-gradient(circle, #E9C46A, transparent)" }} />

        {/* Vastu Compass Watermark */}
        <div
          className="absolute right-0 top-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-5"
          style={{
            backgroundImage: "radial-gradient(circle at center, #D27C2C 1px, transparent 1px), radial-gradient(circle at center, #D27C2C 1px, transparent 1px)",
            backgroundSize: "30px 30px",
            borderRadius: "50%",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 grid md:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 animate-fade-in-up"
              style={{ background: "rgba(210, 124, 44, 0.2)", border: "1px solid rgba(210, 124, 44, 0.4)" }}>
              <Compass size={14} style={{ color: "#E9C46A" }} />
              <span className="font-body text-xs font-semibold tracking-widest uppercase" style={{ color: "#E9C46A" }}>
                Ancient Wisdom. Modern Living.
              </span>
            </div>

            <h1
              className="font-heading text-5xl md:text-7xl font-light leading-[1.1] mb-6 animate-fade-in-up delay-100"
              style={{ color: "white" }}
              data-testid="hero-heading"
            >
              Your Dream Home,
              <br />
              <span className="gradient-text">Blessed by Vastu</span>
            </h1>

            <p
              className="font-body text-lg leading-relaxed mb-8 animate-fade-in-up delay-200"
              style={{ color: "rgba(255,255,255,0.7)" }}
            >
              Get a professionally crafted, Vastu-compliant floor plan tailored to your 
              plot — designed by certified experts who understand the sacred geometry of 
              harmonious living.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-300">
              <Link
                to="/get-floor-plan"
                className="btn-primary"
                data-testid="hero-cta-btn"
              >
                Get Your Floor Plan
                <ArrowRight size={20} />
              </Link>
              <a
                href="#about"
                className="btn-secondary"
                style={{ borderColor: "rgba(255,255,255,0.3)", color: "rgba(255,255,255,0.9)" }}
                data-testid="hero-learn-more-btn"
              >
                Learn About Vastu
              </a>
            </div>

            {/* Trust indicators */}
            <div className="flex items-center gap-6 mt-10 animate-fade-in-up delay-400">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} fill="#E9C46A" stroke="none" />
                  ))}
                </div>
                <span className="font-body text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>
                  4.9/5 from 500+ clients
                </span>
              </div>
              <div className="h-4 w-px bg-white/20" />
              <div className="flex items-center gap-2">
                <CheckCircle2 size={14} style={{ color: "#E9C46A" }} />
                <span className="font-body text-xs" style={{ color: "rgba(255,255,255,0.6)" }}>
                  Certified Vastu Experts
                </span>
              </div>
            </div>
          </div>

          {/* Right: Vastu Compass Diagram */}
          <div className="hidden md:flex items-center justify-center animate-fade-in-up delay-200">
            <div className="relative w-[420px] h-[420px]">
              {/* Outer ring */}
              <div
                className="absolute inset-0 rounded-full border"
                style={{ borderColor: "rgba(210, 124, 44, 0.2)", background: "rgba(210, 124, 44, 0.03)" }}
              />
              {/* Middle ring */}
              <div
                className="absolute inset-8 rounded-full border"
                style={{ borderColor: "rgba(233, 196, 106, 0.3)", background: "rgba(233, 196, 106, 0.04)" }}
              />
              {/* Inner ring */}
              <div
                className="absolute inset-16 rounded-full flex items-center justify-center"
                style={{ background: "rgba(210, 124, 44, 0.1)", border: "1px solid rgba(210, 124, 44, 0.3)" }}
              >
                <div className="text-center">
                  <p className="font-heading text-4xl font-light gradient-text">Om</p>
                  <p className="font-body text-xs mt-1" style={{ color: "rgba(255,255,255,0.5)" }}>Brahmasthan</p>
                </div>
              </div>

              {/* Direction labels */}
              {[
                { dir: "N", x: "50%", y: "4%", color: "#3A5A40" },
                { dir: "S", x: "50%", y: "92%", color: "#D27C2C" },
                { dir: "E", x: "88%", y: "50%", color: "#E9A030" },
                { dir: "W", x: "8%", y: "50%", color: "#6B9AC4" },
                { dir: "NE", x: "82%", y: "12%", color: "#2D7D46" },
                { dir: "NW", x: "12%", y: "12%", color: "#7B9E87" },
                { dir: "SE", x: "82%", y: "84%", color: "#C26B2B" },
                { dir: "SW", x: "12%", y: "84%", color: "#8B6914" },
              ].map(({ dir, x, y, color }) => (
                <div
                  key={dir}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full flex items-center justify-center font-body font-bold text-sm"
                  style={{ left: x, top: y, background: `${color}20`, border: `1px solid ${color}50`, color }}
                >
                  {dir}
                </div>
              ))}

              {/* Cross lines */}
              <svg className="absolute inset-0 w-full h-full" style={{ opacity: 0.2 }}>
                <line x1="50%" y1="0" x2="50%" y2="100%" stroke="#D27C2C" strokeWidth="1" strokeDasharray="4,4" />
                <line x1="0" y1="50%" x2="100%" y2="50%" stroke="#D27C2C" strokeWidth="1" strokeDasharray="4,4" />
                <line x1="15%" y1="15%" x2="85%" y2="85%" stroke="#E9C46A" strokeWidth="0.5" strokeDasharray="4,8" />
                <line x1="85%" y1="15%" x2="15%" y2="85%" stroke="#E9C46A" strokeWidth="0.5" strokeDasharray="4,8" />
              </svg>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <span className="font-body text-xs tracking-widest uppercase" style={{ color: "rgba(255,255,255,0.4)" }}>Scroll</span>
          <ChevronDown size={16} style={{ color: "rgba(255,255,255,0.4)" }} />
        </div>
      </section>

      {/* ─── STATS BAR ────────────────────────────────────────── */}
      <section
        ref={statsRef}
        className="py-14 border-y border-vastu-border"
        style={{ background: "white" }}
        data-testid="stats-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: projects, suffix: "+", label: "Floor Plans Delivered", icon: Home },
              { value: clients, suffix: "+", label: "Happy Clients", icon: Users },
              { value: cities, suffix: "+", label: "Cities Served", icon: MapPin },
              { value: experts, suffix: "+", label: "Vastu Experts", icon: Award },
            ].map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div key={i} className="flex flex-col items-center" data-testid={`stat-${i}`}>
                  <Icon size={24} className="mb-2" style={{ color: "#D27C2C" }} />
                  <span className="font-heading text-4xl md:text-5xl font-medium" style={{ color: "#2D2A26" }}>
                    {stat.value.toLocaleString()}{stat.suffix}
                  </span>
                  <span className="font-body text-sm text-vastu-mutedFg mt-1">{stat.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── ABOUT VASTU ──────────────────────────────────────── */}
      <section id="about" className="py-24 md:py-32 bg-vastu-cream paper-texture" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            {/* Left */}
            <div>
              <p className="section-eyebrow mb-4">Ancient Indian Science</p>
              <h2 className="font-heading text-4xl md:text-5xl font-light leading-tight mb-6" style={{ color: "#2D2A26" }}>
                What is{" "}
                <span className="gradient-text">Vastu Shastra</span>
                ?
              </h2>
              <div className="section-divider mb-8" />
              <p className="font-body text-base md:text-lg text-vastu-mutedFg leading-relaxed mb-6">
                Vastu Shastra (वास्तु शास्त्र) is a 5,000-year-old Indian system of architecture 
                that creates harmony between humans and their built environment. Rooted in ancient 
                Vedic texts, it provides guidelines for designing homes that promote health, 
                happiness, and prosperity.
              </p>
              <p className="font-body text-base md:text-lg text-vastu-mutedFg leading-relaxed mb-8">
                The word "Vastu" means dwelling or building site, and "Shastra" means science or 
                knowledge. Together, Vastu Shastra is the sacred science of creating living spaces 
                that are in perfect alignment with the forces of nature — the five elements, 
                the magnetic field of the Earth, and the cosmic energy of the universe.
              </p>

              {/* 5 Elements */}
              <div className="grid grid-cols-5 gap-3" data-testid="five-elements">
                {[
                  { element: "Earth", symbol: "♁", color: "#8B6914", desc: "Stability" },
                  { element: "Water", symbol: "♒", color: "#6B9AC4", desc: "Flow" },
                  { element: "Fire", symbol: "♌", color: "#D27C2C", desc: "Energy" },
                  { element: "Air", symbol: "♃", color: "#3A5A40", desc: "Movement" },
                  { element: "Space", symbol: "✧", color: "#9B59B6", desc: "Expansion" },
                ].map((el) => (
                  <div
                    key={el.element}
                    className="flex flex-col items-center p-3 rounded-xl text-center"
                    style={{ background: `${el.color}10`, border: `1px solid ${el.color}30` }}
                  >
                    <span className="text-2xl mb-1" style={{ color: el.color }}>{el.symbol}</span>
                    <p className="font-body text-xs font-semibold" style={{ color: el.color }}>{el.element}</p>
                    <p className="font-body text-xs text-vastu-mutedFg">{el.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Image */}
            <div className="relative">
              <div
                className="absolute -top-6 -right-6 w-full h-full rounded-2xl"
                style={{ background: "linear-gradient(135deg, rgba(210, 124, 44, 0.1), rgba(58, 90, 64, 0.1))", border: "1px solid rgba(210, 124, 44, 0.2)" }}
              />
              <img
                src="https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=800&q=80"
                alt="Beautiful Vastu compliant home architecture"
                className="relative rounded-2xl w-full h-[500px] object-cover shadow-2xl"
              />
              {/* Floating badge */}
              <div
                className="absolute -bottom-6 -left-6 rounded-2xl p-5 shadow-xl"
                style={{ background: "white", border: "1px solid rgba(210, 124, 44, 0.2)" }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: "rgba(210, 124, 44, 0.1)" }}>
                    <Award size={24} style={{ color: "#D27C2C" }} />
                  </div>
                  <div>
                    <p className="font-body font-bold text-vastu-charcoal text-sm">5000+</p>
                    <p className="font-body text-xs text-vastu-mutedFg">Years of Tradition</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── VASTU PRINCIPLES ─────────────────────────────────── */}
      <section
        id="principles"
        className="py-24 md:py-32"
        style={{ background: "#f7f5ef" }}
        data-testid="principles-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="section-eyebrow mb-4">The 8 Directions</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-tight" style={{ color: "#2D2A26" }}>
              Vastu Principles for{" "}
              <span className="gradient-text">Every Direction</span>
            </h2>
            <div className="section-divider mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vastuPrinciples.map((principle, i) => {
              const Icon = principle.icon;
              return (
                <div
                  key={i}
                  className="vastu-card hover-lift"
                  style={{ animationDelay: `${i * 0.1}s` }}
                  data-testid={`principle-card-${i}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: principle.bg, border: `1px solid ${principle.color}30` }}
                    >
                      <Icon size={22} style={{ color: principle.color }} />
                    </div>
                    <div>
                      <p
                        className="font-body font-bold text-lg"
                        style={{ color: principle.color }}
                      >
                        {principle.direction}
                      </p>
                      <p className="font-body text-xs text-vastu-mutedFg">{principle.deity}</p>
                    </div>
                  </div>
                  <p className="font-body text-sm text-vastu-mutedFg leading-relaxed mb-3">
                    {principle.desc}
                  </p>
                  <div
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full w-fit"
                    style={{ background: principle.bg }}
                  >
                    <Home size={12} style={{ color: principle.color }} />
                    <span className="font-body text-xs font-medium" style={{ color: principle.color }}>
                      {principle.rooms}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─────────────────────────────────────── */}
      <section id="how-it-works" className="py-24 md:py-32 bg-vastu-cream paper-texture" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="section-eyebrow mb-4">Simple Process</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-tight" style={{ color: "#2D2A26" }}>
              How VastuNet{" "}
              <span className="gradient-text">Works</span>
            </h2>
            <div className="section-divider mx-auto mt-6" />
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connecting lines */}
            <div className="hidden md:block absolute top-16 left-1/3 right-1/3 h-0.5"
              style={{ background: "linear-gradient(to right, #D27C2C, #E9C46A)" }} />

            {howItWorks.map((step, i) => {
              const Icon = step.icon;
              return (
                <div
                  key={i}
                  className="vastu-card text-center hover-lift"
                  data-testid={`how-step-${i}`}
                >
                  <div
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5"
                    style={{ background: `${step.color}15`, border: `1px solid ${step.color}30` }}
                  >
                    <Icon size={28} style={{ color: step.color }} />
                  </div>
                  <span
                    className="font-heading text-5xl font-light opacity-10 absolute top-4 right-4"
                    style={{ color: step.color }}
                  >
                    {step.step}
                  </span>
                  <h3 className="font-heading text-2xl font-medium mb-3" style={{ color: "#2D2A26" }}>
                    {step.title}
                  </h3>
                  <p className="font-body text-sm text-vastu-mutedFg leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── BENEFITS ─────────────────────────────────────────── */}
      <section
        className="py-24 md:py-32 relative"
        style={{ background: "linear-gradient(135deg, #2D2A26 0%, #3A5A40 100%)" }}
        data-testid="benefits-section"
      >
        <div className="absolute inset-0 blueprint-grid opacity-20" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="section-eyebrow mb-4" style={{ color: "#E9C46A" }}>Why Choose Vastu</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-tight text-white">
              Transform Your Home,
              <span className="gradient-text"> Transform Your Life</span>
            </h2>
            <div className="section-divider mx-auto mt-6" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, i) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={i}
                  className="rounded-2xl p-6 hover-lift cursor-default"
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    backdropFilter: "blur(8px)",
                  }}
                  data-testid={`benefit-card-${i}`}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                    style={{ background: `${benefit.color}25` }}
                  >
                    <Icon size={22} style={{ color: benefit.color }} />
                  </div>
                  <h3 className="font-heading text-xl font-medium text-white mb-2">
                    {benefit.title}
                  </h3>
                  <p className="font-body text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.65)" }}>
                    {benefit.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─────────────────────────────────────── */}
      <section className="py-24 md:py-32 bg-vastu-cream paper-texture" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="section-eyebrow mb-4">Client Stories</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-tight" style={{ color: "#2D2A26" }}>
              Real Families,{" "}
              <span className="gradient-text">Real Transformations</span>
            </h2>
            <div className="section-divider mx-auto mt-6" />
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="vastu-card hover-lift"
                data-testid={`testimonial-${i}`}
              >
                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} size={16} fill="#E9C46A" stroke="none" />
                  ))}
                </div>

                {/* Quote */}
                <p className="font-body text-sm text-vastu-mutedFg leading-relaxed mb-6 italic">
                  "{t.text}"
                </p>

                {/* Author */}
                <div className="flex items-center gap-3 pt-4 border-t border-vastu-border">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-heading font-medium text-lg flex-shrink-0"
                    style={{ background: "linear-gradient(135deg, #D27C2C, #E9C46A)" }}
                  >
                    {t.name[0]}
                  </div>
                  <div>
                    <p className="font-body font-semibold text-sm text-vastu-charcoal">{t.name}</p>
                    <p className="font-body text-xs text-vastu-mutedFg flex items-center gap-1">
                      <MapPin size={10} /> {t.location}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ──────────────────────────────────────────────── */}
      <section
        id="faq"
        className="py-24 md:py-32"
        style={{ background: "#f7f5ef" }}
        data-testid="faq-section"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="section-eyebrow mb-4">Got Questions?</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light leading-tight" style={{ color: "#2D2A26" }}>
              Frequently Asked{" "}
              <span className="gradient-text">Questions</span>
            </h2>
            <div className="section-divider mx-auto mt-6" />
          </div>

          <div className="flex flex-col gap-4">
            {faqs.map((faq, i) => (
              <FAQItem
                key={i}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === i}
                onClick={() => setOpenFAQ(openFAQ === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ────────────────────────────────────────── */}
      <section
        className="py-24 md:py-32 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #D27C2C 0%, #b86a24 50%, #3A5A40 100%)" }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 blueprint-grid opacity-20" />
        <div className="absolute top-0 left-0 w-full h-full"
          style={{ background: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }}
        />

        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8"
            style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)" }}
          >
            <Compass size={14} className="text-white" />
            <span className="font-body text-xs font-semibold tracking-widest uppercase text-white">
              Start Your Journey Today
            </span>
          </div>

          <h2
            className="font-heading text-4xl md:text-6xl font-light leading-tight text-white mb-6"
            data-testid="cta-heading"
          >
            Ready to Build Your
            <br />
            <span style={{ color: "#FFF8E1" }}>Vastu-Blessed Home?</span>
          </h2>

          <p
            className="font-body text-lg mb-10 max-w-2xl mx-auto"
            style={{ color: "rgba(255,255,255,0.8)" }}
          >
            Join 1,800+ families who have transformed their lives with Vastu-compliant 
            floor plans. Share your plot details and receive a personalized plan in 24-48 hours.
          </p>

          <Link
            to="/get-floor-plan"
            className="inline-flex items-center gap-3 px-10 py-5 rounded-full font-body font-semibold text-lg transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl"
            style={{
              background: "white",
              color: "#D27C2C",
              boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
            }}
            data-testid="final-cta-btn"
          >
            Get Your Floor Plan Now
            <ArrowRight size={22} />
          </Link>

          <div className="flex justify-center items-center gap-8 mt-12">
            <div className="flex items-center gap-2 text-white/70">
              <CheckCircle2 size={16} className="text-white" />
              <span className="font-body text-sm">Free Consultation</span>
            </div>
            <div className="flex items-center gap-2 text-white/70">
              <CheckCircle2 size={16} className="text-white" />
              <span className="font-body text-sm">24-48 Hour Turnaround</span>
            </div>
            <div className="flex items-center gap-2 text-white/70">
              <CheckCircle2 size={16} className="text-white" />
              <span className="font-body text-sm">Certified Experts</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FOOTER ───────────────────────────────────────────── */}
      <footer className="py-16 bg-vastu-charcoal" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "#D27C2C" }}>
                  <Home size={18} className="text-white" />
                </div>
                <span className="font-heading text-2xl font-semibold text-white">
                  Vastu<span style={{ color: "#D27C2C" }}>Net</span>
                </span>
              </div>
              <p className="font-body text-sm leading-relaxed max-w-xs" style={{ color: "rgba(255,255,255,0.5)" }}>
                Bringing the ancient wisdom of Vastu Shastra to modern homebuilders across India. 
                Every plan we design harmonizes your space with nature's energy.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <p className="font-body font-semibold text-white mb-4 text-sm tracking-wide">Quick Links</p>
              <div className="flex flex-col gap-3">
                {["About Vastu", "Principles", "How It Works", "FAQ"].map((link) => (
                  <a
                    key={link}
                    href={`#${link.toLowerCase().replace(" ", "-")}`}
                    className="font-body text-sm transition-colors duration-300"
                    style={{ color: "rgba(255,255,255,0.5)" }}
                    onMouseEnter={(e) => (e.target.style.color = "#D27C2C")}
                    onMouseLeave={(e) => (e.target.style.color = "rgba(255,255,255,0.5)")}
                  >
                    {link}
                  </a>
                ))}
              </div>
            </div>

            {/* Contact */}
            <div>
              <p className="font-body font-semibold text-white mb-4 text-sm tracking-wide">Get in Touch</p>
              <div className="flex flex-col gap-3">
                <Link
                  to="/get-floor-plan"
                  className="font-body text-sm font-medium px-4 py-2 rounded-full text-center transition-all duration-300"
                  style={{ background: "#D27C2C", color: "white" }}
                  data-testid="footer-cta-btn"
                >
                  Request Floor Plan
                </Link>
                <p className="font-body text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>
                  Response within 24-48 hours
                </p>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div
            className="pt-8 border-t flex flex-col md:flex-row items-center justify-between gap-4"
            style={{ borderColor: "rgba(255,255,255,0.1)" }}
          >
            <p className="font-body text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>
              © 2026 VastuNet. All rights reserved. Harmonizing Spaces, Enriching Lives.
            </p>
            <p className="font-body text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>
              Built with reverence for ancient wisdom.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
